import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Alert, CircularProgress, Dialog, DialogContent, Link, Typography } from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';

import * as actions from '../../../store/actions';
import { StyledButton } from '../../../components';
import { isFullString, isObject, translate } from '../../../shared/utility';
import { pickLocalization } from './pickLocalization';
import { useStyles } from './style';

/**
 * The blocking dialog asking the user to accept the updated privacy policy.
 *
 * Only an explicit `false` opens it. An absent flag means the backend did not
 * say, and blocking the portal on an unknown answer would be worse than showing
 * the dialog one session late.
 *
 * Built on the MUI dialog rather than on ActionDialog, because that wrapper
 * always renders a decline button and passes `onClose` straight through, and
 * the only way out of this one is accepting.
 */
const PrivacyPolicyDialog = props => {
	const {
		currentUser,
		language,
		privacyPolicyDocument,
		acceptedPrivacyPolicy,
		onFetchPrivacyPolicyDocument,
		onAcceptPrivacyPolicy,
		onFetchCurrentUser
	} = props;

	const classes = useStyles();

	const { data: currentUserData } = currentUser;
	const { data: documentData } = privacyPolicyDocument;
	const { data: acceptedData, loading: acceptLoading, error: acceptError } = acceptedPrivacyPolicy;

	const isOutstanding = isObject(currentUserData) && currentUserData.hasAcceptedPrivacyPolicy === false;

	// The acceptance is recorded server side before this flips, so it only
	// covers the gap until the refreshed user arrives.
	const [isAccepted, setIsAccepted] = useState(false);

	useEffect(() => {
		if (isOutstanding && !isObject(documentData)) {
			onFetchPrivacyPolicyDocument();
		}
	}, [isOutstanding]);

	useEffect(() => {
		if (acceptedData) {
			setIsAccepted(true);
			onFetchCurrentUser();
		}
	}, [acceptedData]);

	if (!isOutstanding || isAccepted) {
		return null;
	}

	const policyUrl = pickLocalization(documentData?.localizations, language)?.url;

	// The app's convention is to surface the API's own message; the translated
	// line is the fallback for a failure that carries none. A 401 never gets
	// here: handleResponses claims it first and renews or signs the user out.
	const errorMessage = isFullString(acceptError?.message) ? acceptError.message :
		isFullString(acceptError) ? acceptError :
			translate('views.privacyPolicy.dialog.update.error', language);

	return (
		<Dialog
			classes={{ paper: classes.paper }}
			disableEscapeKeyDown
			open
		>
			<DialogContent className={classes.content}>
				<div className={classes.badge}>
					<SecurityIcon fontSize='large' />
				</div>

				<Typography variant='h4'>
					{translate('views.privacyPolicy.dialog.update.title', language)}
				</Typography>

				<Typography color='textSecondary' variant='body2'>
					{translate('views.privacyPolicy.dialog.update.description', language)}
				</Typography>

				{acceptError ? (
					<Alert severity='error' sx={{ width: 1 }}>
						{errorMessage}
					</Alert>
				) : null}

				<StyledButton
					className={classes.button}
					disabled={acceptLoading}
					onClick={onAcceptPrivacyPolicy}
					size='large'
					variant='contained-primary'
				>
					{acceptLoading ?
						<CircularProgress color='inherit' disableShrink size={19} /> :
						translate('views.privacyPolicy.dialog.update.confirm', language)}
				</StyledButton>

				{policyUrl ? (
					<Link href={policyUrl} rel='noopener noreferrer' target='_blank' variant='body2'>
						{translate('views.privacyPolicy.dialog.update.readFullPolicy', language)}
					</Link>
				) : null}

				<Typography color='textSecondary' variant='caption'>
					{translate('views.privacyPolicy.dialog.update.disclaimer', language)}
				</Typography>
			</DialogContent>
		</Dialog>
	);
};

const detailsShape = PropTypes.shape({
	data: PropTypes.any,
	loading: PropTypes.bool,
	error: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
});

PrivacyPolicyDialog.propTypes = {
	currentUser: detailsShape,
	privacyPolicyDocument: detailsShape,
	acceptedPrivacyPolicy: detailsShape,
	language: PropTypes.string,
	onFetchPrivacyPolicyDocument: PropTypes.func,
	onAcceptPrivacyPolicy: PropTypes.func,
	onFetchCurrentUser: PropTypes.func
};

const mapStateToProps = state => {
	return {
		currentUser: state.details.currentUser,
		privacyPolicyDocument: state.details.privacyPolicyDocument,
		acceptedPrivacyPolicy: state.details.acceptedPrivacyPolicy,
		// GLOBAL PROPS
		language: state.global.language
	};
};

const mapDispatchToProps = dispatch => {
	return {
		onFetchPrivacyPolicyDocument: () => dispatch(actions.fetchPrivacyPolicyDocument()),
		onAcceptPrivacyPolicy: () => dispatch(actions.acceptPrivacyPolicy()),
		onFetchCurrentUser: () => dispatch(actions.fetchCurrentUser())
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(PrivacyPolicyDialog);
